import React, { useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import ConfirmDialog from '../../shared/components/ConfirmDialog';
import { exportBundle, parseCsv, mapClientCsvRows } from '../../shared/importExport';

// Data management (E4): full-business JSON export/import + client CSV
// import with duplicate detection. Imports create records through the
// normal DataProvider add() path so access rules + hooks still apply.
export default function DataManagement() {
  const { data, add } = useData();
  const { notify } = useToast();
  const [mode, setMode] = useState('none'); // 'none' | 'json' | 'csv'
  const [bundle, setBundle] = useState(null);
  const [csvClients, setCsvClients] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [dupPolicy, setDupPolicy] = useState('skip'); // 'skip' | 'import'
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  async function readJson(file) {
    const text = await file.text();
    const parsed = JSON.parse(text);
    if (parsed?.format !== 'portal-export') {
      throw new Error('Not a portal export file');
    }
    setBundle(parsed);
    setMode('json');
  }

  async function readCsv(file) {
    const text = await file.text();
    const rows = mapClientCsvRows(parseCsv(text));
    if (!rows.length) throw new Error('No client rows found (need a Name column)');
    const dupes = rows.filter((row) => data.clients.some((c) => c.email && c.email === row.email));
    setDuplicates(dupes);
    setCsvClients(rows);
    setMode('csv');
  }

  async function onFile(file) {
    if (!file) return;
    try {
      if (file.name.endsWith('.json')) {
        await readJson(file);
      } else if (file.name.endsWith('.csv')) {
        await readCsv(file);
      } else {
        notify('Choose a .json or .csv file', 'error');
      }
    } catch (err) {
      notify(err.message || 'Could not read the file', 'error');
    }
  }

  async function runJsonImport() {
    setBusy(true);
    const counts = {};
    const errors = [];
    try {
      // Clients first — later records reference them by email.
      const idByEmail = {};
      for (const client of bundle.clients || []) {
        try {
           
          const created = await add('clients', client);
          idByEmail[created.email] = created.id;
          counts.clients = (counts.clients || 0) + 1;
        } catch (err) {
          errors.push(`client ${client.name}: ${err.message}`);
        }
      }
      // Existing clients (already present) still map for child records.
      for (const existing of data.clients) {
        if (existing.email) idByEmail[existing.email] = existing.id;
      }
      const resolveClient = (clientRef) => idByEmail[clientRef.email] || data.clients.find((c) => c.id === clientRef.id)?.id;

      for (const job of bundle.jobs || []) {
        try {
           
          await add('jobs', { ...job, client_id: resolveClient(job) || data.clients[0]?.id });
          counts.jobs = (counts.jobs || 0) + 1;
        } catch (err) {
          errors.push(`job ${job.title}: ${err.message}`);
        }
      }
      for (const invoice of bundle.invoices || []) {
        try {
           
          await add('invoices', {
            client_id: resolveClient(invoice) || data.clients[0]?.id,
            job_id: invoice.job_id || null,
            docType: invoice.doc_type || 'invoice',
            number: invoice.number,
            title: invoice.title,
            status: invoice.status,
            due_date: invoice.due_date,
            line_items: (invoice.line_items || []).map((item) => ({ description: item.description, quantity: item.qty, unit_price: item.unit_price })),
          });
          counts.invoices = (counts.invoices || 0) + 1;
        } catch (err) {
          errors.push(`invoice ${invoice.number}: ${err.message}`);
        }
      }
      for (const keyDate of bundle.keyDates || []) {
        try {
           
          await add('keyDates', { ...keyDate, client_id: resolveClient(keyDate) || data.clients[0]?.id });
          counts.keyDates = (counts.keyDates || 0) + 1;
        } catch (err) {
          errors.push(`key date: ${err.message}`);
        }
      }
      for (const note of bundle.notes || []) {
        try {
           
          await add('notes', { ...note, client_id: resolveClient(note) || data.clients[0]?.id });
          counts.notes = (counts.notes || 0) + 1;
        } catch (err) {
          errors.push(`note: ${err.message}`);
        }
      }
      for (const message of bundle.messages || []) {
        try {
           
          await add('messages', { ...message, client_id: resolveClient(message) || data.clients[0]?.id });
          counts.messages = (counts.messages || 0) + 1;
        } catch (err) {
          errors.push(`message: ${err.message}`);
        }
      }

      setReport({ counts, errors });
      notify('Import finished — see the report below');
    } finally {
      setBusy(false);
      setMode('none');
      setBundle(null);
    }
  }

  async function runCsvImport() {
    setBusy(true);
    let created = 0;
    let skipped = 0;
    const errors = [];
    try {
      for (const client of csvClients) {
        const isDup = data.clients.some((c) => c.email && c.email === client.email);
        if (isDup && dupPolicy === 'skip') {
          skipped += 1;
          continue;
        }
        try {
          await add('clients', { ...client });
          created += 1;
        } catch (err) {
          errors.push(`${client.name}: ${err.message}`);
        }
      }
      setReport({ counts: { clients_created: created, duplicates_skipped: skipped }, errors });
      notify('CSV import finished');
    } finally {
      setBusy(false);
      setMode('none');
      setCsvClients([]);
      setDuplicates([]);
    }
  }

  return (
    <section className="panel settings-form">
      <h3>Data management</h3>
      <p className="field-hint">
        Export the full business dataset (clients, jobs, invoices, payments, key dates, messages,
        documents metadata, notes) as JSON, or each list as CSV. Files (photos/PDFs) stay in storage —
        only metadata is exported.
      </p>

      <div className="config-row">
        <strong>Full business export</strong>
        <button
          className="primary-button"
          onClick={() => {
            exportBundle(data);
            notify('Export downloaded');
          }}
        >
          Export everything (JSON)
        </button>
      </div>

      <div className="config-row">
        <strong>Import</strong>
        <label className="secondary-button import-label">
          Choose .json or .csv
          <input
            type="file"
            accept=".json,.csv"
            hidden
            onChange={(event) => {
              onFile(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </label>
      </div>

      {report && (
        <div className="import-report">
          <strong>Last import</strong>
          <ul>
            {Object.entries(report.counts).map(([key, value]) => (
              <li key={key}>{key}: {value}</li>
            ))}
          </ul>
          {report.errors.length > 0 && (
            <details>
              <summary>{report.errors.length} row error(s)</summary>
              <ul>{report.errors.map((err) => <li key={err}>{err}</li>)}</ul>
            </details>
          )}
        </div>
      )}

      <ConfirmDialog
        open={mode === 'json'}
        title="Import full business data?"
        message={bundle ? `This will create ${bundle.clients?.length || 0} clients, ${bundle.jobs?.length || 0} jobs, ${bundle.invoices?.length || 0} billing documents, ${bundle.keyDates?.length || 0} key dates, ${bundle.messages?.length || 0} messages and ${bundle.notes?.length || 0} notes. Existing records are kept.` : ''}
        confirmLabel="Run import"
        onClose={() => { setMode('none'); setBundle(null); }}
        onConfirm={runJsonImport}
      />

      {mode === 'csv' && (
        <div className="csv-import-preview">
          <h4>Import {csvClients.length} clients</h4>
          {duplicates.length > 0 && (
            <label className="config-row">
              <span>{duplicates.length} duplicate email(s) found — policy</span>
              <select value={dupPolicy} onChange={(e) => setDupPolicy(e.target.value)}>
                <option value="skip">Skip duplicates</option>
                <option value="import">Import anyway</option>
              </select>
            </label>
          )}
          <table>
            <thead><tr><th>Name</th><th>Email</th><th>Phone</th></tr></thead>
            <tbody>
              {csvClients.slice(0, 10).map((row, index) => (
                <tr key={`${row.email}-${index}`}>
                  <td>{row.name}</td><td>{row.email}</td><td>{row.phone}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {csvClients.length > 10 && <p className="field-hint">…and {csvClients.length - 10} more</p>}
          <div className="drawer-actions">
            <button className="secondary-button" onClick={() => setMode('none')}>Cancel</button>
            <button className="primary-button" disabled={busy} onClick={runCsvImport}>
              {busy ? 'Importing…' : `Import ${csvClients.length} clients`}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
